﻿import { useCallback, useEffect, useState } from 'react'
import { sileo } from 'sileo'
import queueService from '../services/queueService'
import { invalidateTicketCatalogs } from './useTicketCatalogs'

/**
 * Gestión de las colas de atención (Configuración → Colas): carga y acciones
 * con sus avisos.
 *
 * @returns {{
 *   queues: Array<Object>,
 *   loading: boolean,
 *   saving: boolean,
 *   save: (draft: Object, id?: number|null) => Promise<boolean>,
 *   toggleActive: (queue: Object) => Promise<void>,
 *   remove: (queue: Object) => Promise<void>
 * }}
 */
export default function useQueues() {
  const [queues, setQueues] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [reloadToken, setReloadToken] = useState(0)

  useEffect(() => {
    let active = true
    queueService.getAll().then(({ success, data, error }) => {
      if (!active) return
      if (success) setQueues(data)
      else sileo.error({ title: 'Error cargando colas', description: error })
      setLoading(false)
    })
    return () => {
      active = false
    }
  }, [reloadToken])

  // Cualquier cambio en las colas invalida el catálogo compartido de tickets.
  const reload = useCallback(() => {
    invalidateTicketCatalogs()
    setReloadToken((t) => t + 1)
  }, [])

  /** Crea (sin `id`) o actualiza una cola. */
  const save = useCallback(
    async (draft, id = null) => {
      setSaving(true)
      const payload = { ...draft, name: draft.name.trim() }
      const { success, error } = id
        ? await queueService.update(id, payload)
        : await queueService.create(payload)
      setSaving(false)

      if (!success) {
        sileo.error({ title: 'No se pudo guardar la cola', description: error })
        return false
      }
      sileo.success({ title: `Cola «${payload.name}» guardada` })
      reload()
      return true
    },
    [reload]
  )

  const toggleActive = useCallback(async (queue) => {
    const { success, error } = await queueService.setActive(
      queue.id,
      !queue.active
    )
    if (!success) {
      sileo.error({ title: 'No se pudo cambiar la cola', description: error })
      return
    }
    invalidateTicketCatalogs()
    setQueues((prev) =>
      prev.map((q) => (q.id === queue.id ? { ...q, active: !q.active } : q))
    )
  }, [])

  const remove = useCallback(async (queue) => {
    const { success, error } = await queueService.remove(queue.id)
    if (!success) {
      sileo.error({ title: 'No se pudo eliminar la cola', description: error })
      return
    }
    invalidateTicketCatalogs()
    setQueues((prev) => prev.filter((q) => q.id !== queue.id))
    sileo.success({ title: `Cola «${queue.name}» eliminada` })
  }, [])

  return { queues, loading, saving, save, toggleActive, remove }
}
